import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  Checkbox, CircularProgress, Chip, InputAdornment, Pagination
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Visibility, CheckCircle, Warning, ListAlt, List, Search } from '@mui/icons-material';
import { fetchData } from '../../../apiService';
import { formatDate } from '../../../utils/dateUtils';

const PurchaseModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  purchaseDetails,
  setPurchaseDetails,
  onSave,
  onVerify,
  onToGoodReceipt,
  suppliers,
  items,
  branches,
  loading,
  freightPackingGST,
  companyId,
  token,
  setLoading,
  setLoadingMessage,
  onFetchItems
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  // GstCalculationType: 1 = Excluding Tax, 2 = Including Tax
  const [gstType, setGstType] = useState(formData.GstCalculationType || 1);

  // Vendor Items Modal State
  const [vendorItemsModalOpen, setVendorItemsModalOpen] = useState(false);
  const [vendorItems, setVendorItems] = useState([]);
  const [selectedVendorItems, setSelectedVendorItems] = useState([]);
  const [loadingVendorItems, setLoadingVendorItems] = useState(false);

  // Get Items Modal State
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [itemsPerPage] = useState(50);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Purchase' : modalAction === 'edit' ? 'Edit Purchase' : 'View Purchase';

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
    }
  }, [open]);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') return 'Bill Number is required';
        return '';
      case 'BillDate':
        if (!value) return 'Bill Date is required';
        return '';
      case 'MasterId':
        if (!value) return 'Supplier is required';
        return '';
      case 'StockBranchId':
        if (!value) return 'Stock Branch is required';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;

    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;

    const supplierError = validateField('MasterId', formData.MasterId);
    if (supplierError) newErrors.MasterId = supplierError;

    const stockBranchError = validateField('StockBranchId', formData.StockBranchId);
    if (stockBranchError) newErrors.StockBranchId = stockBranchError;

    // Purchase details validation
    if (purchaseDetails.length === 0) {
      newErrors.purchaseDetails = 'At least one item is required';
    } else {
      const detailErrors = [];
      purchaseDetails.forEach((detail, index) => {
        const itemErrors = {};
        if (!detail.ItemId) itemErrors.ItemId = 'Item is required';
        if (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) {
          itemErrors.ReceivedQuantity = 'Quantity must be greater than 0';
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.Rate = 'Rate must be greater than 0';
        }
        if (Object.keys(itemErrors).length > 0) {
          detailErrors[index] = itemErrors;
        }
      });
      if (detailErrors.length > 0) {
        newErrors.details = detailErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ BillNumber: true, BillDate: true, MasterId: true, StockBranchId: true });

    // Build validation error messages
    const validationMessages = [];

    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push('Bill Number is required');
    }
    if (!formData.BillDate) {
      validationMessages.push('Bill Date is required');
    }
    if (!formData.MasterId) {
      validationMessages.push('Supplier is required');
    }
    if (!formData.StockBranchId) {
      validationMessages.push('Stock Branch is required');
    }
    if (purchaseDetails.length === 0) {
      validationMessages.push('At least one item is required');
    } else {
      // Check each item for Received Qty and Rate
      const itemErrors = [];
      purchaseDetails.forEach((detail, index) => {
        if (!detail.ItemId) {
          itemErrors.push(`Row ${index + 1}: Item is required`);
        }
        if (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) {
          itemErrors.push(`Row ${index + 1}: Received Qty is required`);
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.push(`Row ${index + 1}: Rate is required`);
        }
      });
      if (itemErrors.length > 0) {
        validationMessages.push(...itemErrors);
      }
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  // Sync gstType with formData
  useEffect(() => {
    if (formData.GstCalculationType) {
      setGstType(formData.GstCalculationType);
    }
  }, [formData.GstCalculationType]);

  // Recalculate when GST type changes
  useEffect(() => {
    if (!isViewMode && purchaseDetails.length > 0) {
      const useIGST = isIGSTInvoice();
      const updatedDetails = purchaseDetails.map(d => {
        const receivedQty = Math.max(0, parseFloat(d.ReceivedQuantity) || 0);
        const rate = Math.max(0, parseFloat(d.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(d.GSTRate) || 0);
        const discountPct = Math.max(0, parseFloat(d.DiscountPercentage) || 0);

        let taxableAmount, gstAmount, amount;

        if (isIncludingTax()) {
          // Rate includes GST - calculate backwards
          const totalWithGst = receivedQty * rate;
          const baseAmount = totalWithGst / (1 + gstRate / 100);
          const discountAmount = (baseAmount * discountPct) / 100;
          taxableAmount = baseAmount - discountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
          amount = baseAmount;
        } else {
          // Rate excludes GST - normal calculation
          amount = receivedQty * rate;
          const discountAmount = (amount * discountPct) / 100;
          taxableAmount = amount - discountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
        }

        return {
          ...d,
          Amount: amount,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      });
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [gstType, formData.InvoiceTypeName]);

  const handleGstTypeChange = (value) => {
    const intValue = parseInt(value);
    setGstType(intValue);
    const gstTypeName = intValue === 2 ? 'Including Tax' : 'Excluding Tax';
    setFormData(prev => ({ ...prev, GstCalculationType: intValue, GstCalculationTypeName: gstTypeName }));
  };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    if (field === 'MasterId') {
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(value));
      const invoiceTypeName = selectedSupplier?.InvoiceTypeName || selectedSupplier?.InvoiceType || '';
      const gstNumber = selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || '';

      // Don't clear purchase items table when supplier changes - keep existing items

      setFormData(prev => ({ ...prev, [field]: value, InvoiceTypeName: invoiceTypeName, GSTNumber: gstNumber }));
    } else if (field === 'PaidAmount') {
      // Validate Paid Amount should not exceed Total Amount
      const paidValue = parseFloat(value) || 0;
      const totalAmount = parseFloat(formData.TotalAmount) || 0;
      if (paidValue > totalAmount) {
        showError('Paid Amount cannot be greater than Total Amount');
        return;
      }
      setFormData(prev => ({
        ...prev,
        [field]: value,
        BalanceAmount: totalAmount - paidValue
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const isIGSTInvoice = () => {
    const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
    return invoiceType.includes('igst');
  };

  // Fetch Vendor Items
  const fetchVendorItems = async (masterId) => {
    if (!masterId || !companyId || !token) return;

    setLoadingVendorItems(true);

    try {
      const response = await fetchData('PurchaseApi', `/VendorWiseItemDetails/${companyId}/${masterId}`, token);
      const vendorItemsData = response.response_data || response || [];
      setVendorItems(Array.isArray(vendorItemsData) ? vendorItemsData : []);
      setSelectedVendorItems([]);
      return true;
    } catch (error) {
      console.error('Error fetching vendor items:', error);
      setVendorItems([]);
      showError('Failed to fetch vendor items');
      return false;
    } finally {
      setLoadingVendorItems(false);
    }
  };

  // Open Vendor Items Modal
  const handleOpenVendorItemsModal = async () => {
    if (!formData.MasterId) {
      showError('Please select a supplier first');
      return;
    }

    // Open modal first, then fetch data
    setVendorItemsModalOpen(true);
    await fetchVendorItems(formData.MasterId);
  };

  // Fetch Vendor Items and pre-select items already in purchase table
  useEffect(() => {
    if (vendorItemsModalOpen && vendorItems.length > 0) {
      // Get item IDs that are already in the purchase table
      const existingItemIds = purchaseDetails.map(d => d.ItemId);
      // Pre-select items that are already in the table
      const preSelectedItems = vendorItems
        .filter(item => existingItemIds.includes(item.ItemId || item.Id))
        .map(item => item.ItemId || item.Id);
      setSelectedVendorItems(preSelectedItems);
    }
  }, [vendorItemsModalOpen, vendorItems, purchaseDetails]);

  // Close Vendor Items Modal
  const handleCloseVendorItemsModal = () => {
    setVendorItemsModalOpen(false);
    setVendorItems([]);
    setSelectedVendorItems([]);
  };

  // Toggle Vendor Item Selection
  const handleToggleVendorItem = (itemId) => {
    setSelectedVendorItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Select All Vendor Items
  const handleSelectAllVendorItems = () => {
    if (selectedVendorItems.length === vendorItems.length) {
      setSelectedVendorItems([]);
    } else {
      setSelectedVendorItems(vendorItems.map(item => item.ItemId || item.Id));
    }
  };

  // Add Selected Vendor Items to Purchase
  const handleAddVendorItemsToPurchase = () => {
    const itemsToAdd = vendorItems.filter(item =>
      selectedVendorItems.includes(item.ItemId || item.Id)
    );

    const newPurchaseDetails = itemsToAdd.map(item => {
      const itemId = item.ItemId || item.Id;

      // Use LastPurchaseRateByVendor if available and > 0, otherwise use Rate/SaleRate
      const lastRate = parseFloat(item.LastPurchaseRateByVendor || 0);
      const defaultRate = parseFloat(item.Rate || item.SaleRate || 0);
      const rate = lastRate > 0 ? lastRate : defaultRate;

      const gstRate = parseFloat(item.GSTRate || item.GST || 0);
      const qty = 1; // Default quantity

      const amount = qty * rate;
      const discountPct = 0;
      const discountAmt = 0;
      const taxableAmount = amount - discountAmt;
      const gstAmount = (taxableAmount * gstRate) / 100;
      const useIGST = isIGSTInvoice();

      return {
        Id: 0,
        ItemId: itemId,
        ItemName: item.ItemName || item.Name,
        HSNCode: item.HSNCode || item.HSN || '',
        ReceivedQuantity: qty,
        DamageQuantity: 0,
        StockQuantity: qty,
        Rate: rate,
        Amount: amount,
        DiscountPercentage: discountPct,
        DiscountAmount: discountAmt,
        GSTRate: gstRate,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount
      };
    });

    // Add to existing purchase details (avoid duplicates)
    const existingItemIds = purchaseDetails.map(d => d.ItemId);
    const uniqueNewItems = newPurchaseDetails.filter(item => !existingItemIds.includes(item.ItemId));

    if (uniqueNewItems.length > 0) {
      setPurchaseDetails(prev => [...prev, ...uniqueNewItems]);
      setSnackbar({ open: true, message: `${uniqueNewItems.length} item(s) added successfully`, severity: 'success' });
    }

    const duplicateCount = newPurchaseDetails.length - uniqueNewItems.length;
    if (duplicateCount > 0 && uniqueNewItems.length === 0) {
      setSnackbar({ open: true, message: `All selected items are already in the table`, severity: 'info' });
    } else if (duplicateCount > 0) {
      setSnackbar({ open: true, message: `${uniqueNewItems.length} item(s) added. ${duplicateCount} item(s) already exist`, severity: 'info' });
    }

    handleCloseVendorItemsModal();
  };

  // Get Items Modal Functions
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      if (onFetchItems) {
        await onFetchItems('');
      } else {
        console.warn('onFetchItems function not provided');
      }
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.PurchaseRate || item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    // Validate discount percentage not above 100%
    if (field === 'selectedDiscountPercentage') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 100) return;
    }

    // Validate discount amount doesn't exceed maximum allowed (100% of applicable amount)
    if (field === 'selectedDiscountAmount') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const selectedItem = selectedItems.find(item => item.Id === itemId);
        if (selectedItem) {
          const quantity = Math.max(0, parseFloat(selectedItem.selectedQuantity) || 0);
          const rate = Math.max(0, parseFloat(selectedItem.selectedRate) || 0);
          const gstRate = Math.max(0, parseFloat(selectedItem.GSTRate) || 0);

          let maxDiscountAmount;
          if (isIncludingTax()) {
            // For inclusive tax, discount is applied on base amount before GST
            const totalWithGst = quantity * rate;
            maxDiscountAmount = totalWithGst / (1 + gstRate / 100);
          } else {
            // For exclusive tax, discount is applied on amount
            maxDiscountAmount = quantity * rate;
          }

          if (numValue > maxDiscountAmount) {
            // Block the input if it exceeds 100% of applicable amount
            return;
          }
        }
      }
    }

    setSelectedItems(prev => prev.map(item => {
      if (item.Id === itemId) {
        const updatedItem = { ...item, [field]: value };

        // Calculate bidirectional sync for discount fields
        if (field === 'selectedDiscountPercentage' || field === 'selectedDiscountAmount' || field === 'selectedQuantity' || field === 'selectedRate') {
          const quantity = Math.max(0, parseFloat(updatedItem.selectedQuantity) || 0);
          const rate = Math.max(0, parseFloat(updatedItem.selectedRate) || 0);
          const gstRate = Math.max(0, parseFloat(updatedItem.GSTRate) || 0);

          let baseAmount;
          if (isIncludingTax()) {
            // For inclusive tax, discount is applied on base amount before GST
            const totalWithGst = quantity * rate;
            baseAmount = totalWithGst / (1 + gstRate / 100);
          } else {
            // For exclusive tax, discount is applied on amount
            baseAmount = quantity * rate;
          }

          if (field === 'selectedDiscountPercentage') {
            // When discount % changes, update discount amount
            const discountPct = Math.min(Math.max(0, parseFloat(value) || 0), 100);
            const discountAmount = (baseAmount * discountPct) / 100;
            updatedItem.selectedDiscountPercentage = discountPct;
            updatedItem.selectedDiscountAmount = Math.round(discountAmount * 100) / 100;
          } else if (field === 'selectedDiscountAmount') {
            // When discount amount changes, update discount %
            const discountAmount = Math.min(Math.max(0, parseFloat(value) || 0), baseAmount);
            const discountPct = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
            updatedItem.selectedDiscountAmount = Math.round(discountAmount * 100) / 100;
            updatedItem.selectedDiscountPercentage = Math.round(discountPct * 100) / 100;
          } else if (field === 'selectedQuantity' || field === 'selectedRate') {
            // When quantity or rate changes, recalculate discount amount based on current %
            const discountPct = Math.min(Math.max(0, parseFloat(updatedItem.selectedDiscountPercentage) || 0), 100);
            const discountAmount = (baseAmount * discountPct) / 100;
            updatedItem.selectedDiscountAmount = Math.round(discountAmount * 100) / 100;

            // If discount amount would exceed new base amount, adjust both
            if (discountAmount > baseAmount) {
              updatedItem.selectedDiscountAmount = baseAmount;
              updatedItem.selectedDiscountPercentage = 100;
            }
          }
        }

        return updatedItem;
      }
      return item;
    }));
  };

  // Add selected items to purchase details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const useIGST = isIGSTInvoice();

      // Check which items are already added
      const existingItemIds = purchaseDetails.map(d => d.ItemId);
      const itemsToAdd = selectedItems.filter(
        selectedItem => !existingItemIds.includes(selectedItem.Id)
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const newPurchaseDetails = itemsToAdd.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const gstRate = parseFloat(selectedItem.GSTRate) || 0;
        const discountPct = Math.min(parseFloat(selectedItem.selectedDiscountPercentage) || 0, 100);
        const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;

        let amount, taxableAmount, gstAmount, finalDiscountAmount;

        if (isIncludingTax()) {
          // Rate includes GST - calculate backwards
          const totalWithGst = quantity * rate;
          const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
          finalDiscountAmount = discountAmt > 0 ? Math.min(discountAmt, baseAmountBeforeGST) : (baseAmountBeforeGST * discountPct) / 100;
          taxableAmount = baseAmountBeforeGST - finalDiscountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
          amount = baseAmountBeforeGST;
        } else {
          // Rate excludes GST - normal calculation
          amount = quantity * rate;
          finalDiscountAmount = discountAmt > 0 ? Math.min(discountAmt, amount) : (amount * discountPct) / 100;
          taxableAmount = amount - finalDiscountAmount;
          gstAmount = (taxableAmount * gstRate) / 100;
        }

        return {
          Id: 0,
          ItemId: selectedItem.Id,
          ItemName: selectedItem.Name,
          HSNCode: selectedItem.HSNCode || '',
          ReceivedQuantity: quantity,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: quantity,
          BilledQuantity: quantity,
          OrderQuantity: quantity,
          ReturnDamageQuantity: 0,
          ReturnStockQuantity: 0,
          Rate: rate,
          Amount: amount,
          DiscountPercentage: discountAmt > 0 ? (finalDiscountAmount / (isIncludingTax() ? (quantity * rate) / (1 + gstRate / 100) : amount)) * 100 : discountPct,
          DiscountAmount: finalDiscountAmount,
          GSTRate: gstRate,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount,
          IsActive: true
        };
      });

      setPurchaseDetails(prev => [...prev, ...newPurchaseDetails]);
      calculateTotals([...purchaseDetails, ...newPurchaseDetails]);

      handleCloseGetItemsModal();

      setSnackbar({
        open: true,
        message: `${newPurchaseDetails.length} item(s) added successfully`,
        severity: 'success'
      });

    } catch (error) {
      console.error('Error adding selected items:', error);
      setSnackbar({
        open: true,
        message: 'Error adding items. Please try again.',
        severity: 'error'
      });
    } finally {
      setAddingItemsLoading(false);
    }
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Memoized filtered items for better performance - only show Trading Items (IsTradingItem) and Raw Materials (IsRawMaterial)
  const filteredItems = React.useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    // First filter: only show items where IsTradingItem or IsRawMaterial is true
    const tradingAndRawItems = items.filter(item => item.IsTradingItem === true || item.IsRawMaterial === true);

    if (!getItemsSearchValue.trim()) {
      return tradingAndRawItems;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return tradingAndRawItems.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm)
    );
  }, [items, getItemsSearchValue]);

  // Get paginated items for performance
  const getPaginatedItems = React.useMemo(() => {
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  // Calculate total pages
  const getTotalPages = React.useMemo(() => {
    return Math.ceil(filteredItems.length / itemsPerPage);
  }, [filteredItems.length, itemsPerPage]);

  // Check if item is already added to purchase (optimized with Set lookup)
  const isItemAlreadyInPurchase = React.useCallback((itemId) => {
    return purchaseDetails.some(detail => detail.ItemId === itemId);
  }, [purchaseDetails]);
  const isIncludingTax = () => gstType === 2;

  // Helper function to calculate maximum discount amount
  const getMaxDiscountAmount = (quantity, rate, gstRate) => {
    const qty = parseFloat(quantity) || 0;
    const rt = parseFloat(rate) || 0;
    const gst = parseFloat(gstRate) || 0;

    if (isIncludingTax()) {
      // For inclusive tax, discount is applied on base amount before GST
      const totalWithGst = qty * rt;
      return totalWithGst / (1 + gst / 100);
    } else {
      // For exclusive tax, discount is applied on amount
      return qty * rt;
    }
  };

  const handleDetailChange = (index, field, value) => {
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedDetails = [...purchaseDetails];

    if (['ReceivedQuantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setPurchaseDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;

      // Validate discount percentage not above 100%
      if (field === 'DiscountPercentage') {
        if (numValue > 100) return;
      }

      // Validate discount amount doesn't exceed maximum allowed (100% of item amount)
      if (field === 'DiscountAmount') {
        const currentDetail = updatedDetails[index];
        const receivedQty = Math.max(0, parseFloat(currentDetail.ReceivedQuantity) || 0);
        const rate = Math.max(0, parseFloat(currentDetail.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(currentDetail.GSTRate) || 0);

        let maxDiscountAmount;
        if (isIncludingTax()) {
          // For inclusive tax, discount is applied on base amount before GST
          const totalWithGst = receivedQty * rate;
          const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
          maxDiscountAmount = baseAmountBeforeGST;
        } else {
          // For exclusive tax, discount is applied on amount
          maxDiscountAmount = receivedQty * rate;
        }

        if (numValue > maxDiscountAmount) {
          // Block the input if it exceeds 100% of applicable amount
          return;
        }
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['ReceivedQuantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
      // In create/edit mode, stock quantity equals received quantity (damage/excess/less are set during verification)
      const stockQty = receivedQty;
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);
      const useIGST = isIGSTInvoice();

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax()) {
        // Rate includes GST - calculate backwards
        const totalWithGst = receivedQty * rate;
        const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          // Ensure discount amount doesn't exceed 100% of base amount
          discountAmount = Math.min(discountAmount, baseAmountBeforeGST);
          discountPct = baseAmountBeforeGST > 0 ? (discountAmount / baseAmountBeforeGST) * 100 : 0;
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
        } else {
          discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
          discountAmount = (baseAmountBeforeGST * discountPct) / 100;
        }

        taxableAmount = baseAmountBeforeGST - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
        amount = baseAmountBeforeGST; // Amount before GST and discount

        updatedDetails[index] = {
          ...updatedDetails[index],
          StockQuantity: stockQty,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      } else {
        // Rate excludes GST - normal calculation
        amount = receivedQty * rate;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          // Ensure discount amount doesn't exceed 100% of amount
          discountAmount = Math.min(discountAmount, amount);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
        } else {
          discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
          // Ensure discount percentage doesn't exceed 100%
          discountPct = Math.min(discountPct, 100);
          discountAmount = (amount * discountPct) / 100;
        }

        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          StockQuantity: stockQty,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      }
    }
    setPurchaseDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = (index, itemId) => {
    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...purchaseDetails];
      const receivedQty = parseFloat(updatedDetails[index].ReceivedQuantity) || 0;
      const rate = selectedItem.PurchaseRate || selectedItem.SaleRate || 0;
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;
      const useIGST = isIGSTInvoice();

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax()) {
        // Rate includes GST - calculate backwards
        const totalWithGst = receivedQty * rate;
        const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
        const discountAmount = (baseAmountBeforeGST * discountPct) / 100;
        taxableAmount = baseAmountBeforeGST - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
        amount = baseAmountBeforeGST;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: gstRate,
          Rate: rate,
          Amount: amount,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountAmount: discountAmount,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      } else {
        // Rate excludes GST
        amount = receivedQty * rate;
        const discountAmount = (amount * discountPct) / 100;
        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: gstRate,
          Rate: rate,
          Amount: amount,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountAmount: discountAmount,
          TaxableAmount: taxableAmount,
          CGSTAmount: useIGST ? 0 : gstAmount / 2,
          SGSTAmount: useIGST ? 0 : gstAmount / 2,
          IGSTAmount: useIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount
        };
      }
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  };

  const addDetailRow = () => {
    setHasUnsavedChanges(true);
    setPurchaseDetails([...purchaseDetails, {
      Id: 0, ItemId: '', ItemName: '', BatchNumber: '', PurchaseOrderId: null,
      OrderQuantity: 0, ReceivedQuantity: 0, BilledQuantity: 0,
      DamageQuantity: 0, ExcessQuantity: 0, LessQuantity: 0, StockQuantity: 0,
      ReturnDamageQuantity: 0, ReturnStockQuantity: 0,
      Rate: 0, Amount: 0, DiscountPercentage: 0, DiscountAmount: 0,
      HSNCode: '', GSTRate: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true
    }]);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = purchaseDetails.filter((_, i) => i !== index);
    setPurchaseDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;
    // Use user-entered GST rate if provided, otherwise fall back to API default or max item rate
    const effectiveFreightGSTRate = parseFloat(formData.FreightGSTRate) || Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = parseFloat(formData.PackingGSTRate) || Math.max(defaultPackingGSTRate, maxItemGSTRate);

    const freightAmount = parseFloat(formData.FreightAmount) || 0;
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingAmount = parseFloat(formData.PackingAmount) || 0;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;
    const tcsAmount = parseFloat(formData.TCSAmount) || 0;
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const totalAmount = Math.round(grandTotal);
    const paidAmount = parseFloat(formData.PaidAmount) || 0;
    const balanceAmount = totalAmount - paidAmount;

    setFormData(prev => ({
      ...prev, ...totals,
      FreightGSTRate: effectiveFreightGSTRate,
      FreightGSTAmount: freightGSTAmount,
      PackingGSTRate: effectivePackingGSTRate,
      PackingGSTAmount: packingGSTAmount,
      RoundOff: roundOff,
      TotalAmount: totalAmount,
      BalanceAmount: balanceAmount
    }));
  };

  useEffect(() => {
    // Only auto-calculate totals in create mode
    if (!isViewMode && modalAction === 'create') {
      calculateTotals(purchaseDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, formData.FreightGSTRate, formData.PackingGSTRate, formData.TCSAmount, formData.PaidAmount]);

  // Recalculate totals in edit mode when freight/packing amounts change (user input)
  useEffect(() => {
    if (!isViewMode && modalAction === 'edit' && purchaseDetails.length > 0) {
      calculateTotals(purchaseDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, formData.FreightGSTRate, formData.PackingGSTRate, formData.TCSAmount]);

  useEffect(() => {
    if (!isViewMode && purchaseDetails.length > 0 && formData.InvoiceTypeName) {
      const useIGST = isIGSTInvoice();
      const updatedDetails = purchaseDetails.map(d => {
        const gstRate = parseFloat(d.GSTRate) || 0;
        const taxableAmount = parseFloat(d.TaxableAmount) || 0;
        const gstAmount = (taxableAmount * gstRate) / 100;
        return { ...d, CGSTAmount: useIGST ? 0 : gstAmount / 2, SGSTAmount: useIGST ? 0 : gstAmount / 2, IGSTAmount: useIGST ? gstAmount : 0 };
      });
      setPurchaseDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [formData.InvoiceTypeName]);

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getSelectedSupplierGST = () => {
    if (formData.GSTNumber) return formData.GSTNumber;
    if (!formData.MasterId) return null;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return supplier?.GSTNumber || supplier?.GSTNo || null;
  };

  const getSelectedSupplierInvoiceType = () => {
    if (formData.InvoiceTypeName) return formData.InvoiceTypeName;
    if (!formData.MasterId) return null;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return supplier?.InvoiceTypeName || supplier?.InvoiceType || null;
  };

  const getSupplierDisplayName = () => {
    if (formData.MasterName) return formData.MasterName;
    if (!formData.MasterId) return '';
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return supplier?.Name || '';
  };

  const supplierGSTNo = getSelectedSupplierGST();
  const supplierInvoiceType = getSelectedSupplierInvoiceType();
  const supplierDisplayName = getSupplierDisplayName();

  const disabledFieldStyle = { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } };
  const compactInput = { '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' }, ...(isViewMode && disabledFieldStyle) };

  return (
    <Dialog open={open} onClose={(e, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        if (hasUnsavedChanges && !isViewMode) {
          setCancelConfirmOpen(true);
          return;
        }
      }
      onClose();
    }}
      maxWidth="xl" fullWidth fullScreen={isMobile} disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1600px', width: '95vw' } }}>

      <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart sx={{ color: '#1e40af', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
          </Box>
          <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#fff' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Purchase Information</Typography>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={1.2}>
            <TextField
              fullWidth
              size="small"
              label="Bill No *"
              value={formData.BillNumber || ''}
              onChange={(e) => handleChange('BillNumber', e.target.value)}
              onBlur={() => handleBlur('BillNumber')}
              disabled={isViewMode}
              error={touched.BillNumber && !!errors.BillNumber}
              helperText={touched.BillNumber && errors.BillNumber}
              sx={compactInput}
            />
          </Grid>
          <Grid item xs={6} sm={1.3}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="Bill Date"
                value={formatDate(formData.BillDate) || '-'} disabled
                InputLabelProps={{ shrink: true }} sx={compactInput} />
            ) : (
              <TextField
                fullWidth size="small" type="date" label="Bill Date *"
                value={formData.BillDate || ''}
                onChange={(e) => handleChange('BillDate', e.target.value)}
                onBlur={() => handleBlur('BillDate')}
                error={touched.BillDate && !!errors.BillDate}
                helperText={touched.BillDate && errors.BillDate}
                InputLabelProps={{ shrink: true }} sx={compactInput}
              />
            )}
          </Grid>
          <Grid item xs={6} sm={2.5}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="Supplier" value={supplierDisplayName || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                <FormControl fullWidth size="small" error={touched.MasterId && !!errors.MasterId} sx={compactInput}>
                  <InputLabel>Supplier *</InputLabel>
                  <Select
                    value={formData.MasterId || ''}
                    onChange={(e) => handleChange('MasterId', e.target.value)}
                    onBlur={() => handleBlur('MasterId')}
                    disabled={isViewMode}
                    label="Supplier *"
                    sx={{ fontSize: '0.8rem' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                  >
                    {suppliers.map(s => <MenuItem key={s.MasterId || s.Id} value={s.MasterId || s.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>{s.Name}</MenuItem>)}
                  </Select>
                  {touched.MasterId && errors.MasterId && <FormHelperText>{errors.MasterId}</FormHelperText>}
                </FormControl>
                {formData.MasterId && (
                  <IconButton
                    size="small"
                    onClick={handleOpenVendorItemsModal}
                    sx={{
                      mt: 0.5,
                      bgcolor: '#dbeafe',
                      color: '#1e40af',
                      '&:hover': { bgcolor: '#bfdbfe' },
                      width: 32,
                      height: 32
                    }}
                    title="View Vendor Items"
                  >
                    <ListAlt fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={6} sm={1.8}>
            <TextField fullWidth size="small" label="GST No" value={supplierGSTNo || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierGSTNo ? '#f0fdf4' : '#f9fafb' } }} />
          </Grid>
          <Grid item xs={6} sm={1}>
            <TextField fullWidth size="small" label="Inv Type" value={supplierInvoiceType || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: supplierInvoiceType ? '#fef3c7' : '#f9fafb' } }} />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="GST Type" value={gstType === 2 ? 'Including Tax' : 'Excluding Tax'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: '#e0f2fe' } }} />
            ) : (
              <FormControl fullWidth size="small" sx={compactInput}>
                <InputLabel>GST Type *</InputLabel>
                <Select
                  value={gstType}
                  onChange={(e) => handleGstTypeChange(e.target.value)}
                  label="GST Type *"
                  sx={{ fontSize: '0.8rem', bgcolor: '#e0f2fe' }}
                >
                  <MenuItem value={1} sx={{ fontSize: '0.75rem', py: 0.5 }}>Excluding Tax</MenuItem>
                  <MenuItem value={2} sx={{ fontSize: '0.75rem', py: 0.5 }}>Including Tax</MenuItem>
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={6} sm={1.3}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="Clearing Date"
                value={formatDate(formData.ClearingDate) || '-'} disabled
                InputLabelProps={{ shrink: true }} sx={compactInput} />
            ) : (
              <TextField fullWidth size="small" type="date" label="Clearing Date"
                value={formData.ClearingDate || ''}
                onChange={(e) => handleChange('ClearingDate', e.target.value)}
                InputLabelProps={{ shrink: true }} sx={compactInput} />
            )}
          </Grid>
          <Grid item xs={6} sm={1.4}>
            {isViewMode ? (
              <TextField
                fullWidth size="small" label="Stock Branch"
                value={
                  (branches || []).find(b => (b.Id || b.BranchId) === formData.StockBranchId)?.Name ||
                  (branches || []).find(b => (b.Id || b.BranchId) === formData.StockBranchId)?.BranchName ||
                  (formData.StockBranchId ? formData.StockBranchId : '-')
                }
                disabled
                sx={{ ...compactInput, ...disabledFieldStyle }}
              />
            ) : (
              <FormControl fullWidth size="small" sx={compactInput} error={touched.StockBranchId && !!errors.StockBranchId}>
                <InputLabel>Stock Branch *</InputLabel>
                <Select
                  value={formData.StockBranchId || ''}
                  onChange={(e) => handleChange('StockBranchId', e.target.value)}
                  onBlur={() => handleBlur('StockBranchId')}
                  label="Stock Branch *"
                  sx={{ fontSize: '0.8rem', bgcolor: '#f0fdf4' }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.85rem', py: 0.5 }}><em>None</em></MenuItem>
                  {(branches || []).map(b => (
                    <MenuItem key={b.Id || b.BranchId} value={b.Id || b.BranchId} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                      {b.Name || b.BranchName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.StockBranchId && errors.StockBranchId && <FormHelperText>{errors.StockBranchId}</FormHelperText>}
              </FormControl>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: errors.purchaseDetails ? '#dc2626' : '#1e40af' }}>Purchase Items *</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>({purchaseDetails.length} items)</Typography>
            {gstType === 2 && (
              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, bgcolor: '#d1fae5', px: 0.5, borderRadius: 0.5 }}>Tax Inclusive</Typography>
            )}
            {errors.purchaseDetails && (
              <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.7rem' }}>({errors.purchaseDetails})</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {purchaseDetails.length > 5 && (
              <Button
                size="small"
                onClick={() => setItemsModalOpen(true)}
                startIcon={<Visibility sx={{ fontSize: 14 }} />}
                sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e40af' }}
              >
                View All
              </Button>
            )}
            {!isViewMode && !formData.IsVerified && (
              <>
                <Button
                  size="small"
                  startIcon={<List sx={{ fontSize: 14 }} />}
                  onClick={handleOpenGetItemsModal}
                  variant="text"
                  sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#059669' }}
                >
                  Get Items
                </Button>
                {/* <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addDetailRow} variant="outlined" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}>Add</Button> */}
              </>
            )}
          </Box>
        </Box>

        {/* Desktop Table View - Fixed height for 5 rows */}
        {!isMobile && (
          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 1.5, border: errors.purchaseDetails ? '1px solid #dc2626' : '1px solid #e5e7eb', borderRadius: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '4%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Sl No</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '15%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Item</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '6%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Rcvd Qty</th>
                  {isViewMode && (
                    <>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '6%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Dmg Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '6%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Excess Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '6%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Less Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '6%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Stock Qty</th>
                    </>
                  )}
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '8%' : '10%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Rate</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '6%' : '8%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Disc%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '8%' : '10%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Disc Amt</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '6%' : '7%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>GST%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '8%' : '9%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>GST Amt</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '9%' : '10%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Taxable</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', width: isViewMode ? '9%' : '10%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}>Total</th>
                  {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '4%', background: 'rgba(27, 3, 71, 1)', color: 'white' }}></th>}
                </tr>
              </thead>
              <tbody>
                {purchaseDetails.map((d, index) => (
                  <tr key={index}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#6b7280' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: isViewMode ? 600 : 400 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{(() => { const it = items.find(i => i.Id === parseInt(d.ItemId)); return it ? (it.Code ? `${it.Code} - ${it.ItemName || it.Name}` : (it.ItemName || it.Name)) : '-'; })()}</span>
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{d.ReceivedQuantity || 0}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.ReceivedQuantity ?? ''} onChange={(e) => handleDetailChange(index, 'ReceivedQuantity', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    {isViewMode && (
                      <>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>
                          {d.DamageQuantity || 0}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#059669' }}>
                          {d.ExcessQuantity || 0}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#f59e0b' }}>
                          {d.LessQuantity || 0}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600, color: '#10b981' }}>
                          {(d.ReceivedQuantity || 0) - (d.DamageQuantity || 0) + (d.ExcessQuantity || 0) - (d.LessQuantity || 0)}
                        </td>
                      </>
                    )}
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{d.Rate || 0}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{d.DiscountPercentage || 0}</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.DiscountPercentage ?? 0}
                          onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)}
                          disabled={isViewMode}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }}
                          sx={{ width: '100%' }}
                          title="Discount percentage (max 100%)"
                        />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600 }}>{d.DiscountAmount || 0}</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.DiscountAmount ?? 0}
                          onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)}
                          disabled={isViewMode}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            max: (parseFloat(d.ReceivedQuantity) || 0) * (parseFloat(d.Rate) || 0),
                            step: 0.01,
                            style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' }
                          }}
                          sx={{ width: '100%' }}
                          title={`Discount amount (max ₹${getMaxDiscountAmount(d.ReceivedQuantity, d.Rate, d.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of ${isIncludingTax() ? 'base' : 'item'} amount)`}
                          placeholder={`Max: ${getMaxDiscountAmount(d.ReceivedQuantity, d.Rate, d.GSTRate).toFixed(2)}`}
                        />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontWeight: 600 }}>{d.GSTRate || 0}%</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 700, color: '#1B5E20' }}>
                      {formatCurrency((parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0) + (parseFloat(d.IGSTAmount) || 0))}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {purchaseDetails.length === 0 && (
                  <tr><td colSpan={isViewMode ? 14 : 11} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No items added</td></tr>
                )}
              </tbody>
            </table>
          </Box>
        )}

        {/* Mobile Card View */}
        {isMobile && (
          <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
            {purchaseDetails.map((d, index) => (
              <Box key={index} sx={{ border: '1px solid #d1d5db', borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                    {items.find(i => i.Id === parseInt(d.ItemId))?.Name || 'Select Item'}
                  </Typography>
                  {!isViewMode && (
                    <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.5, bgcolor: '#fef2f2', color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  )}
                </Box>
                {!isViewMode && ( 
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Select Item</InputLabel>
                    <Select value={d.ItemId || ''} onChange={(e) => handleItemSelect(index, e.target.value)} label="Select Item" sx={{ fontSize: '0.85rem' }} MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}>
                      {items.map(item => <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.80rem', py: 0.5 }}>{item.Name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Batch</Typography>
                    <TextField size="small" value={d.BatchNumber || ''} onChange={(e) => handleDetailChange(index, 'BatchNumber', e.target.value)} disabled={isViewMode} fullWidth sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Rcvd Qty</Typography>
                    <TextField size="small" type="number" value={d.ReceivedQuantity || ''} onChange={(e) => handleDetailChange(index, 'ReceivedQuantity', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Rate</Typography>
                    <TextField size="small" type="number" value={d.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Disc %</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={d.DiscountPercentage || 0}
                      onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)}
                      disabled={isViewMode}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }}
                      title="Discount percentage (max 100%)"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Disc Amt</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={d.DiscountAmount || 0}
                      onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)}
                      disabled={isViewMode}
                      fullWidth
                      inputProps={{
                        min: 0,
                        max: (parseFloat(d.ReceivedQuantity) || 0) * (parseFloat(d.Rate) || 0),
                        step: 0.01
                      }}
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }}
                      title={`Discount amount (max ₹${getMaxDiscountAmount(d.ReceivedQuantity, d.Rate, d.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of ${isIncludingTax() ? 'base' : 'item'} amount)`}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>GST %</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.8rem', textAlign: 'center', py: 0.75 }}>
                      {d.GSTRate || 0}%
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #f3f4f6' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: 120 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>GST Amt:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#1B5E20', fontSize: '0.7rem' }}>
                        {formatCurrency((parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0) + (parseFloat(d.IGSTAmount) || 0))}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: 120 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>Taxable:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.7rem' }}>
                        {formatCurrency(d.TaxableAmount)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block' }}>Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.9rem' }}>
                      {formatCurrency(d.TotalAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {purchaseDetails.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}><Typography variant="body2">No items added</Typography></Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Totals Section */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Additional Charges</Typography>
            <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Freight" type="number" value={formData.FreightAmount || ''} onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Freight GST %" type="number" value={formData.FreightGSTRate || ''} onChange={(e) => handleChange('FreightGSTRate', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, max: 100, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Packing" type="number" value={formData.PackingAmount || ''} onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Packing GST %" type="number" value={formData.PackingGSTRate || ''} onChange={(e) => handleChange('PackingGSTRate', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, max: 100, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="TCS Amount" type="number" value={formData.TCSAmount || ''} onChange={(e) => handleChange('TCSAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Paid Amount" type="number" value={formData.PaidAmount || ''} onChange={(e) => handleChange('PaidAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} multiline rows={2} sx={compactInput} />
                </Grid>
                {/* Audit Info - under Remarks */}
                {(isViewMode || modalAction === 'edit') && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      Created by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#1e40af' }}>{formData.CreatedByName || '-'}</Typography> on{' '}
                      <Typography component="span" variant="caption" sx={{ color: '#059669' }}>
                        {formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      Modified by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#92400e' }}>{formData.ModifiedByName || '-'}</Typography> on{' '}
                      <Typography component="span" variant="caption" sx={{ color: '#92400e' }}>
                        {formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.ModifiedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                      </Typography>
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Summary</Typography>
            <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Subtotal</Typography><Typography variant="caption">{formatCurrency(formData.Amount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Discount</Typography><Typography variant="caption" color={(parseFloat(formData.DiscountAmount) || 0) > 0 ? 'error' : 'text.secondary'}>{(parseFloat(formData.DiscountAmount) || 0) > 0 ? `-${formatCurrency(formData.DiscountAmount)}` : formatCurrency(0)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Taxable</Typography><Typography variant="caption">{formatCurrency(
                (parseFloat(formData.TaxableAmount) || 0) > 0
                  ? formData.TaxableAmount
                  : purchaseDetails.reduce((s, d) => s + (parseFloat(d.TaxableAmount) || 0), 0)
              )}</Typography></Box>
              {!isIGSTInvoice() ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">CGST</Typography><Typography variant="caption">{formatCurrency(formData.CGSTAmount)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">SGST</Typography><Typography variant="caption">{formatCurrency(formData.SGSTAmount)}</Typography></Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">IGST</Typography><Typography variant="caption">{formatCurrency(formData.IGSTAmount)}</Typography></Box>
              )}
              {(parseFloat(formData.FreightAmount) || 0) > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Freight</Typography><Typography variant="caption">{formatCurrency(formData.FreightAmount)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Freight GST ({formData.FreightGSTRate || 0}%)</Typography><Typography variant="caption">{formatCurrency(formData.FreightGSTAmount)}</Typography></Box>
                </>
              )}
              {(parseFloat(formData.PackingAmount) || 0) > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Packing</Typography><Typography variant="caption">{formatCurrency(formData.PackingAmount)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Packing GST ({formData.PackingGSTRate || 0}%)</Typography><Typography variant="caption">{formatCurrency(formData.PackingGSTAmount)}</Typography></Box>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">TCS</Typography><Typography variant="caption">{formatCurrency(formData.TCSAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Round Off</Typography><Typography variant="caption">{formData.RoundOff >= 0 ? '+' : ''}{(formData.RoundOff || 0).toFixed(2)}</Typography></Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(formData.TotalAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption">Paid</Typography><Typography variant="caption" sx={{ color: '#10b981' }}>{formatCurrency(formData.PaidAmount)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Balance</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency((parseFloat(formData.TotalAmount) || 0) - (parseFloat(formData.PaidAmount) || 0))}</Typography></Box>
            </Box>
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
        <Button onClick={handleCancelClick} variant="outlined" size="small">Cancel</Button>
        {isViewMode && formData.Id && !formData.IsConvertToGoodReceipt && !formData.IsVerified && (
          <Button
            onClick={() => onToGoodReceipt && onToGoodReceipt(formData.Id, formData.BillNumber)}
            variant="contained"
            size="small"
            disabled={loading}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
          >
            To Good Receipt
          </Button>
        )}
        {isViewMode && formData.Id && formData.IsConvertToGoodReceipt && !formData.IsVerified && (
          <Button
            onClick={() => onVerify && onVerify(formData.Id)}
            variant="contained"
            size="small"
            disabled={loading}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
          >
            Verify Purchase
          </Button>
        )}
        {!isViewMode && <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}>{modalAction === 'edit' ? 'Update' : 'Save'}</Button>}
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog open={itemsModalOpen} onClose={() => setItemsModalOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '10px', maxHeight: '85vh' } }}>
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>All Purchase Items ({purchaseDetails.length})</Typography>
            </Box>
            <IconButton onClick={() => setItemsModalOpen(false)} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#1e40af' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>#</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: 600 }}>Item Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>HSN</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Rcvd Qty</th>
                  {isViewMode && (
                    <>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Dmg Qty</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Excess Qty</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Less Qty</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Stock Qty</th>
                    </>
                  )}
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>Rate</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>Disc%</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', color: 'white', fontWeight: 600 }}>GST%</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>GST Amt</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>Taxable</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: 600 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {purchaseDetails.map((d, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 500 }}>{index + 1}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500 }}>
                      {/* {items.find(i => i.Id === parseInt(d.ItemId))?.Name || d.ItemName || '-'} */}
                      <span style={{ fontWeight: 600, fontSize: '0.80rem' }}>{(() => { const it = items.find(i => i.Id === parseInt(d.ItemId)); return it ? (it.Code ? `${it.Code} - ${it.ItemName || it.Name}` : (it.ItemName || it.Name)) : '-'; })()}</span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.HSNCode || '-'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>{d.ReceivedQuantity || 0}</td>
                    {isViewMode && (
                      <>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>{d.DamageQuantity || 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#059669' }}>{d.ExcessQuantity || 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#f59e0b' }}>{d.LessQuantity || 0}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#10b981' }}>
                          {(d.ReceivedQuantity || 0) - (d.DamageQuantity || 0) + (d.ExcessQuantity || 0) - (d.LessQuantity || 0)}
                        </td>
                      </>
                    )}
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(d.Rate)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.DiscountPercentage || 0}%</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.GSTRate || 0}%</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1B5E20' }}>
                      {formatCurrency((parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0) + (parseFloat(d.IGSTAmount) || 0))}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(d.TotalAmount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot style={{ position: 'sticky', bottom: 0, background: '#f8fafc' }}>
                <tr style={{ borderTop: '2px solid #1e40af' }}>
                  <td colSpan="3" style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>Totals:</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700 }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0)}</td>
                  {isViewMode && (
                    <>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.DamageQuantity) || 0), 0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#059669' }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ExcessQuantity) || 0), 0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#f59e0b' }}>{purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.LessQuantity) || 0), 0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#10b981' }}>{purchaseDetails.reduce((sum, d) => sum + ((d.ReceivedQuantity || 0) - (d.DamageQuantity || 0) + (d.ExcessQuantity || 0) - (d.LessQuantity || 0)), 0)}</td>
                    </>
                  )}
                  <td colSpan="3"></td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(purchaseDetails.reduce((sum, d) => sum + ((parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0) + (parseFloat(d.IGSTAmount) || 0)), 0))}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0))}</td>
                </tr>
              </tfoot>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={() => setItemsModalOpen(false)} variant="outlined" size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{
              borderColor: '#10b981',
              color: '#10b981',
              '&:hover': { borderColor: '#059669', backgroundColor: '#f0fdf4' }
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Items Modal */}
      <Dialog
        open={vendorItemsModalOpen}
        onClose={handleCloseVendorItemsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ bgcolor: '#f8fafc', py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ListAlt sx={{ color: '#3b82f6', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>
                Vendor Items - {supplierDisplayName}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseVendorItemsModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingVendorItems ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 4,
              minHeight: 300
            }}>
              {/* Modern Skeleton Loading */}
              <Box sx={{ width: '100%', maxWidth: 600 }}>
                {/* Header Skeleton */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  mb: 1
                }}>
                  <Box sx={{
                    width: 150,
                    height: 20,
                    bgcolor: '#e2e8f0',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                  <Box sx={{
                    width: 80,
                    height: 32,
                    bgcolor: '#e2e8f0',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                </Box>

                {/* Table Header Skeleton */}
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ width: 40, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ flex: 1, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 80, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 80, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 80, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                  <Box sx={{ width: 60, height: 16, bgcolor: '#cbd5e1', borderRadius: 0.5 }} />
                </Box>

                {/* Table Rows Skeleton */}
                {[1, 2, 3, 4].map((item, index) => (
                  <Box
                    key={item}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      px: 2,
                      py: 2,
                      borderBottom: '1px solid #e2e8f0',
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      animationDelay: `${index * 0.1}s`,
                      '@keyframes shimmer': {
                        '0%': { opacity: 0.6 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.6 }
                      }
                    }}
                  >
                    <Box sx={{
                      width: 20,
                      height: 20,
                      bgcolor: '#e2e8f0',
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{
                      flex: 1,
                      height: 20,
                      bgcolor: '#e2e8f0',
                      borderRadius: 0.5
                    }} />
                    <Box sx={{
                      width: 80,
                      height: 20,
                      bgcolor: '#e2e8f0',
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{
                      width: 80,
                      height: 20,
                      bgcolor: '#e2e8f0',
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{
                      width: 80,
                      height: 20,
                      bgcolor: '#e2e8f0',
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                    <Box sx={{
                      width: 60,
                      height: 20,
                      bgcolor: '#e2e8f0',
                      borderRadius: 0.5,
                      flexShrink: 0
                    }} />
                  </Box>
                ))}
              </Box>

              {/* Loading Text */}
              <Typography sx={{
                color: '#94a3b8',
                fontSize: '0.875rem',
                fontWeight: 500,
                mt: 3,
                animation: 'fadeInOut 1.5s ease-in-out infinite',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.5 },
                  '50%': { opacity: 1 }
                }
              }}>
                Loading vendor items...
              </Typography>
            </Box>
          ) : vendorItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8' }}>No items found for this vendor</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                  {selectedVendorItems.length} of {vendorItems.length} items selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllVendorItems}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    borderColor: '#cbd5e1',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      bgcolor: '#f1f5f9'
                    }
                  }}
                >
                  {selectedVendorItems.length === vendorItems.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', fontSize: '0.75rem', borderBottom: '2px solid #cbd5e1', width: '50px' }}>
                        <Checkbox
                          checked={selectedVendorItems.length === vendorItems.length && vendorItems.length > 0}
                          indeterminate={selectedVendorItems.length > 0 && selectedVendorItems.length < vendorItems.length}
                          onChange={handleSelectAllVendorItems}
                          size="small"
                          sx={{ p: 0 }}
                        />
                      </th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #cbd5e1' }}>Item Name</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #cbd5e1', width: '110px' }}>HSN</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #cbd5e1', width: '100px' }}>Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #cbd5e1', width: '100px' }}>Last Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #cbd5e1', width: '80px' }}>GST%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorItems.map((item, index) => {
                      const itemId = item.ItemId || item.Id;
                      const isSelected = selectedVendorItems.includes(itemId);
                      return (
                        <tr
                          key={itemId}
                          onClick={() => handleToggleVendorItem(itemId)}
                          style={{
                            cursor: 'pointer',
                            background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#ffffff' : '#f8fafc'),
                            borderBottom: '1px solid #e2e8f0',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = '#f1f5f9';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#f8fafc';
                          }}
                        >
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0 }}
                            />
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '0.8rem', fontWeight: 500, color: '#1e293b' }}>
                            {item.ItemName || item.Name}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                            {item.HSNCode || item.HSN || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                            ₹{(item.Rate || item.SaleRate || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                            ₹{(item.LastPurchaseRateByVendor || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#d1fae5',
                              color: '#065f46',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}>
                              {item.GSTRate || item.GST || 0}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button
            onClick={handleCloseVendorItemsModal}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddVendorItemsToPurchase}
            variant="contained"
            disabled={selectedVendorItems.length === 0}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': {
                bgcolor: '#cbd5e1',
                color: '#94a3b8'
              }
            }}
          >
            Add Selected Items ({selectedVendorItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: isMobile ? 0 : 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.1rem' }}>
                Get Items ({filteredItems.length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: '#fafbfc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#6b7280' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 1 },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      '&:hover': { borderColor: '#1e40af' },
                      '&.Mui-focused': { borderColor: '#1e40af' }
                    }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 10).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Code ? item.Code + ' - ' : ''}${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{
                          fontSize: '0.75rem',
                          bgcolor: '#fff',
                          '& .MuiChip-deleteIcon': { color: '#1565c0' }
                        }}
                      />
                    ))}
                    {selectedItems.length > 10 && (
                      <Chip
                        label={`+${selectedItems.length - 10} more`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)', height: '28px' }}>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '4%', fontSize: '0.65rem', lineHeight: '1' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems.length && getPaginatedItems.length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems.forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems.forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, transform: 'scale(0.8)' }}
                          />
                        </th>
                        <th style={{ padding: '2px 1px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '28%', fontSize: '0.65rem', lineHeight: '1' }}>Item</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>HSN</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem', lineHeight: '1' }}>GST%</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem', lineHeight: '1' }}>Qty</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem', lineHeight: '1' }}>Rate</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem', lineHeight: '1' }}>Disc%</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem', lineHeight: '1' }}>Disc Amt</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem', lineHeight: '1' }}>GST Amt</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems.map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInPurchase(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isSelected ? '#e3f2fd' : (index % 2 === 0 ? '#fff' : '#f8fafc'),
                            opacity: isAlreadyAdded ? 0.6 : 1,
                            transition: 'background-color 0.2s ease'
                          }}>
                            <td style={{ padding: '4px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                                sx={{
                                  color: isSelected ? '#1e40af' : '#9ca3af',
                                  '&.Mui-checked': { color: '#1e40af' }
                                }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px', fontWeight: 500, color: '#1e293b' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: '0.8rem' }}>{item.Code ? `${item.Code} - ${item.Name}` : item.Name}</span>
                                {item.IsTradingItem && (
                                  <Chip
                                    label="Trading"
                                    size="small"
                                    sx={{
                                      bgcolor: '#dbeafe',
                                      color: '#1e40af',
                                      fontSize: '0.55rem',
                                      height: 16,
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                                {item.IsRawMaterial && (
                                  <Chip
                                    label="Raw Material"
                                    size="small"
                                    sx={{
                                      bgcolor: '#fef3c7',
                                      color: '#92400e',
                                      fontSize: '0.55rem',
                                      height: 16,
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                                {isAlreadyAdded && (
                                  <Chip
                                    label="Added"
                                    size="small"
                                    sx={{
                                      bgcolor: '#dcfce7',
                                      color: '#166534',
                                      fontSize: '0.6rem',
                                      height: 16,
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                              </Box>
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                              {item.HSNCode || '-'}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500, color: '#1e40af', fontSize: '0.75rem' }}>
                              {item.GSTRate || 0}%
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{
                                    min: 1,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 60,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>1</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 80,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                                  {formatCurrency(item.PurchaseRate || item.SaleRate || 0)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountPercentage}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    max: 100,
                                    step: 0.01,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 60,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                  title="Discount percentage (max 100%)"
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountAmount}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    max: getMaxDiscountAmount(selectedItem.selectedQuantity, selectedItem.selectedRate, item.GSTRate),
                                    step: 0.01,
                                    style: {
                                      textAlign: 'right',
                                      direction: 'ltr'
                                    }
                                  }}
                                  sx={{
                                    width: 80,
                                    '& input': {
                                      textAlign: 'right !important',
                                      direction: 'ltr !important'
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 0.25,
                                      px: 0.5,
                                      fontSize: '0.75rem',
                                      bgcolor: '#fff',
                                      textAlign: 'right !important'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                  title={`Discount amount (max ₹${getMaxDiscountAmount(selectedItem.selectedQuantity, selectedItem.selectedRate, selectedItem.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of ${isIncludingTax() ? 'base' : 'item'} amount)`}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#1B5E20', fontSize: '0.75rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const gstRate = parseFloat(item.GSTRate) || 0;

                                  let taxableAmount, gstAmount;
                                  if (isIncludingTax()) {
                                    // Rate includes GST - calculate backwards
                                    const totalWithGst = qty * rate;
                                    const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
                                    const finalDiscountAmount = discAmt > 0 ? Math.min(discAmt, baseAmountBeforeGST) : (baseAmountBeforeGST * discPct) / 100;
                                    taxableAmount = baseAmountBeforeGST - finalDiscountAmount;
                                    gstAmount = (taxableAmount * gstRate) / 100;
                                  } else {
                                    // Rate excludes GST - normal calculation
                                    const amount = qty * rate;
                                    const finalDiscountAmount = discAmt > 0 ? Math.min(discAmt, amount) : (amount * discPct) / 100;
                                    taxableAmount = amount - finalDiscountAmount;
                                    gstAmount = (taxableAmount * gstRate) / 100;
                                  }
                                  return formatCurrency(gstAmount);
                                })()
                              ) : (
                                formatCurrency(((item.PurchaseRate || item.SaleRate || 0) * 1 * (parseFloat(item.GSTRate) || 0)) / 100)
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const gstRate = parseFloat(item.GSTRate) || 0;

                                  let taxableAmount, gstAmount;
                                  if (isIncludingTax()) {
                                    // Rate includes GST - calculate backwards
                                    const totalWithGst = qty * rate;
                                    const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
                                    const finalDiscountAmount = discAmt > 0 ? Math.min(discAmt, baseAmountBeforeGST) : (baseAmountBeforeGST * discPct) / 100;
                                    taxableAmount = baseAmountBeforeGST - finalDiscountAmount;
                                    gstAmount = (taxableAmount * gstRate) / 100;
                                  } else {
                                    // Rate excludes GST - normal calculation
                                    const amount = qty * rate;
                                    const finalDiscountAmount = discAmt > 0 ? Math.min(discAmt, amount) : (amount * discPct) / 100;
                                    taxableAmount = amount - finalDiscountAmount;
                                    gstAmount = (taxableAmount * gstRate) / 100;
                                  }
                                  const total = taxableAmount + gstAmount;
                                  return formatCurrency(total);
                                })()
                              ) : (
                                formatCurrency((item.PurchaseRate || item.SaleRate || 0) * 1)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={getTotalPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            size="small"
            sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}
          >
            {addingItemsLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Adding...
              </>
            ) : (
              `Add Selected Items (${selectedItems.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PurchaseModal;

